import React, { useEffect, useMemo, useState } from "react";
import { useFinanceLedger, computeCashflow } from "./finance/useFinanceLedger";
import SupplierAgeing from "./inventory/SupplierAgeing";
import DayCashFlow from "./DayCashFlow";
import LabAgeing from "./labs/LabAgeing";
import { apiGet } from "../../services/api";
import "./AccountantLedger.css";

/**
 * Financial Reports Component (formerly AccountantLedger)
 * True P&L + Cashflow engine using unified finance events
 */
function FinancialReports() {
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { events, loading, error, refresh } = useFinanceLedger(selectedDate);
  const [labs, setLabs] = useState<{ id: number; name: string }[]>([]);
  const [labFilter, setLabFilter] = useState("");
  const [auditFilter, setAuditFilter] = useState<"all" | "internal">("all");
  const [consultantSummary, setConsultantSummary] = useState<
    { consultant: string; service: string; count: number; total_fee: number }[]
  >([]);
  const [collectionDetails, setCollectionDetails] = useState<{
    revenue: { items: { patient_name: string; amount: number }[]; total: number };
    expenditure: {
      suppliers: { items: { supplier_name: string; amount: number; bill_number?: string }[]; total: number };
      labs: { items: { lab_name: string; amount: number; bill_number?: string }[]; total: number };
      consultants: { items: { consultant_name: string; service_name: string; amount: number }[]; total: number };
      total: number;
    };
    net_collection: number;
  } | null>(null);

  useEffect(() => {
    apiGet("/api/manager/labs")
      .then((res: unknown) => setLabs(Array.isArray(res) ? (res as { id: number; name: string }[]) : []))
      .catch(() => setLabs([]));
  }, []);

  useEffect(() => {
    // Fetch consultant summary for selected date (daily)
    apiGet(`/api/manager/consultant-fee-summary?date=${selectedDate}`)
      .then((res: unknown) => setConsultantSummary(Array.isArray(res) ? (res as { consultant: string; service: string; count: number; total_fee: number }[]) : []))
      .catch(() => setConsultantSummary([]));

    // Force refresh of finance events to ensure we have the latest
    refresh();

    // Fetch collection details
    apiGet(`/api/manager/collection-details?date=${selectedDate}`)
      .then((res: unknown) => setCollectionDetails(res as typeof collectionDetails))
      .catch(() => setCollectionDetails(null));
  }, [selectedDate]);

  const filteredEvents = useMemo(() => {
    let list = events; // Already filtered by date at the server level
    if (auditFilter === "internal") {
      list = list.filter((e) => ["CONSULTANT_FEE", "LAB_INTERNAL"].includes(String(e.source || "")));
    }
    return list;
  }, [auditFilter, events]);

  const cashflow = computeCashflow(filteredEvents);
  const displayCashflow = useMemo(() => {
    // Prefer Collection Report totals for the top "Daily Financial Report" cards.
    // This keeps the cards accurate even when finance_events is missing/empty.
    if (auditFilter !== "all") return cashflow;
    if (!collectionDetails) return cashflow;

    const inflow = Number(collectionDetails.revenue?.total ?? 0);
    const outflow = Number(collectionDetails.expenditure?.total ?? 0);
    const net = Number(collectionDetails.net_collection ?? (inflow - outflow));
    return { inflow, outflow, net };
  }, [auditFilter, cashflow, collectionDetails, filteredEvents.length]);

  if (loading) {
    return (
      <div className="ledger-container">
        <div className="loading-state">Loading financial data…</div>
      </div>
    );
  }

  return (
    <div className="ledger-container">
      {error ? (
        <div className="error-state">
          Finance events could not be loaded. Reports may be incomplete until `/api/finance/events` is available.
        </div>
      ) : null}
      <div className="ledger-header">
        <h2>📒 Daily Financial Report</h2>
        <p className="ledger-subtitle">
          View financial performance for a specific day.
        </p>
      </div>

      <div className="section-header">
        <h4 className="section-title">Report Settings</h4>
        <div className="filter-row">
          <label>Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <label style={{ marginLeft: '16px' }}>View</label>
          <select value={auditFilter} onChange={(e) => setAuditFilter(e.target.value as "all" | "internal")}>
            <option value="all">All Sources</option>
            <option value="internal">Internal Audit Only</option>
          </select>
        </div>
      </div>

      <section className="finance-summary">
        <div className="finance-card inflow">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h4>Cash Inflow</h4>
            <p className="card-amount">₹{displayCashflow.inflow.toLocaleString("en-IN")}</p>
          </div>
        </div>

        <div className="finance-card outflow">
          <div className="card-icon">📤</div>
          <div className="card-content">
            <h4>Cash Outflow</h4>
            <p className="card-amount">₹{displayCashflow.outflow.toLocaleString("en-IN")}</p>
          </div>
        </div>

        <div className="finance-card net">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h4>Net Cash</h4>
            <p className={`card-amount ${displayCashflow.net >= 0 ? "positive" : "negative"}`}>
              ₹{displayCashflow.net.toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </section>

      <section className="ledger-section">
        <h4 className="section-title">📊 Collection Report</h4>
        {!collectionDetails ? (
          <div className="empty-state">Loading collection details...</div>
        ) : (
          <div className="collection-details">
            {/* Revenue Section */}
            <div className="collection-section revenue-section">
              <div className="collection-section-header">💰 REVENUE</div>
              {collectionDetails.revenue.items.length === 0 ? (
                <div className="empty-state-small">No patient payments for this date</div>
              ) : (
                <div className="collection-list">
                  {collectionDetails.revenue.items.map((item, idx) => (
                    <div key={idx} className="collection-item-row">
                      <span className="item-name">{item.patient_name}</span>
                      <span className="item-amount">₹{item.amount.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="collection-total">
                <span>Total Revenue:</span>
                <span>₹{collectionDetails.revenue.total.toLocaleString("en-IN")}</span>
              </div>
            </div>

            {/* Expenditure Section */}
            <div className="collection-section expenditure-section">
              <div className="collection-section-header">📤 EXPENDITURE</div>

              {/* A. Supplier Payments */}
              <div className="collection-subsection">
                <h5>A. Supplier Payments</h5>
                {collectionDetails.expenditure.suppliers.items.length === 0 ? (
                  <div className="empty-state-small">No supplier payments</div>
                ) : (
                  <div className="collection-list">
                    {collectionDetails.expenditure.suppliers.items.map((item, idx) => (
                      <div key={idx} className="collection-item-row">
                        <span className="item-name">{item.supplier_name}</span>
                        <span className="item-amount">₹{item.amount.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* B. Lab Payments */}
              <div className="collection-subsection">
                <h5>B. Lab Payments</h5>
                {collectionDetails.expenditure.labs.items.length === 0 ? (
                  <div className="empty-state-small">No lab payments</div>
                ) : (
                  <div className="collection-list">
                    {collectionDetails.expenditure.labs.items.map((item, idx) => (
                      <div key={idx} className="collection-item-row">
                        <span className="item-name">{item.lab_name}</span>
                        <span className="item-amount">₹{item.amount.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* C. Consultant Fees */}
              <div className="collection-subsection">
                <h5>C. Consultant Fees</h5>
                {collectionDetails.expenditure.consultants.items.length === 0 ? (
                  <div className="empty-state-small">No consultant fees</div>
                ) : (
                  <div className="collection-list">
                    {collectionDetails.expenditure.consultants.items.map((item, idx) => (
                      <div key={idx} className="collection-item-row">
                        <span className="item-name">{item.consultant_name} - {item.service_name}</span>
                        <span className="item-amount">₹{item.amount.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="collection-total">
                <span>Total Expenditure:</span>
                <span>₹{collectionDetails.expenditure.total.toLocaleString("en-IN")}</span>
              </div>
            </div>

            {/* Net Collection */}
            <div className={`net-collection-box ${collectionDetails.net_collection >= 0 ? "positive" : "negative"}`}>
              <div className="net-label">📈 NET COLLECTION</div>
              <div className="net-amount">₹{collectionDetails.net_collection.toLocaleString("en-IN")}</div>
              <div className="net-formula">Revenue - Expenditure</div>
            </div>
          </div>
        )}
      </section>



      <section className="ledger-section">
        <h4 className="section-title">🧾 Consultant Fee Summary (Daily)</h4>
        {consultantSummary.length === 0 ? (
          <div className="empty-state">No consultant fees recorded for this date.</div>
        ) : (
          <div className="table-wrapper">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Consultant</th>
                  <th>Service</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Total Fee</th>
                </tr>
              </thead>
              <tbody>
                {consultantSummary.map((row, idx) => (
                  <tr key={`${row.consultant}-${row.service}-${idx}`}>
                    <td>{row.consultant}</td>
                    <td>{row.service}</td>
                    <td className="text-right">{row.count}</td>
                    <td className="text-right">₹{Number(row.total_fee || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ledger-section">
        <h4 className="section-title">⏳ Ageing Analysis</h4>
        <div className="ageing-grid">
          <div className="ageing-panel supplier-panel">
            <h5>📦 Supplier Ageing</h5>
            <SupplierAgeing />
          </div>
          <div className="ageing-panel lab-panel">
            <h5>🧪 Lab Ageing</h5>
            <div className="filter-row" style={{ marginBottom: '1rem' }}>
              <label>Lab</label>
              <select value={labFilter} onChange={(e) => setLabFilter(e.target.value)}>
                <option value="">All Labs</option>
                {labs.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <LabAgeing labId={labFilter || undefined} />
          </div>
        </div>
      </section>


    </div>
  );
}

export default FinancialReports;
